/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CursorPaginatedResponseSupplierListV1Dto } from '../models/cursor-paginated-response-supplier-list-v-1-dto';
import { CursorPaginationRequest } from '../models/cursor-pagination-request';
import { PaginatedResponseSupplierListDto } from '../models/paginated-response-supplier-list-dto';
import { PaginationWithFiltersRequest } from '../models/pagination-with-filters-request';
import { SupplierDuplicateCheckRequestV1 } from '../models/supplier-duplicate-check-request-v-1';
import { SupplierDuplicateCheckResultV1 } from '../models/supplier-duplicate-check-result-v-1';
import { SupplierListV1 } from '../models/supplier-list-v-1';
import { SupplierUpdateCommandV1 } from '../models/supplier-update-command-v-1';


/**
 * SupplierV1 API
 */
@Injectable({
  providedIn: 'root',
})
export class SupplierV1Service extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation deactivateSupplierV1
   */
  static readonly DeactivateSupplierV1Path = '/v1/supplier/{supplierId}/deactivate';

  /**
   * Deactivate a supplier.
   *
   * Deactivates a supplier and all associated supplier users. Idempotent: returns success if supplier is already deactivated.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deactivateSupplierV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  deactivateSupplierV1$Response(params: {
    supplierId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<SupplierListV1>> {

    const rb = new RequestBuilder(this.rootUrl, SupplierV1Service.DeactivateSupplierV1Path, 'post');
    if (params) {
      rb.path('supplierId', params.supplierId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SupplierListV1>;
      })
    );
  }

  /**
   * Deactivate a supplier.
   *
   * Deactivates a supplier and all associated supplier users. Idempotent: returns success if supplier is already deactivated.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deactivateSupplierV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deactivateSupplierV1(params: {
    supplierId: string;
  },
  context?: HttpContext

): Observable<SupplierListV1> {

    return this.deactivateSupplierV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<SupplierListV1>) => r.body as SupplierListV1)
    );
  }

  /**
   * Path part for operation activateSupplierV1
   */
  static readonly ActivateSupplierV1Path = '/v1/supplier/{supplierId}/activate';

  /**
   * Activate a previously deactivated supplier.
   *
   * Activates a previously deactivated supplier. Does not automatically reactivate supplier users. Idempotent: returns success if supplier is already active.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `activateSupplierV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  activateSupplierV1$Response(params: {
    supplierId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<SupplierListV1>> {

    const rb = new RequestBuilder(this.rootUrl, SupplierV1Service.ActivateSupplierV1Path, 'post');
    if (params) {
      rb.path('supplierId', params.supplierId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SupplierListV1>;
      })
    );
  }

  /**
   * Activate a previously deactivated supplier.
   *
   * Activates a previously deactivated supplier. Does not automatically reactivate supplier users. Idempotent: returns success if supplier is already active.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `activateSupplierV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  activateSupplierV1(params: {
    supplierId: string;
  },
  context?: HttpContext

): Observable<SupplierListV1> {

    return this.activateSupplierV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<SupplierListV1>) => r.body as SupplierListV1)
    );
  }

  /**
   * Path part for operation listSuppliersV1
   */
  static readonly ListSuppliersV1Path = '/v1/supplier/list';

  /**
   * List suppliers available for browsing.
   *
   * Deprecated: use /cursor-list instead. Provides list of suppliers. Suppliers are returned as unordered list.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listSuppliersV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   *
   * @deprecated
   */
  listSuppliersV1$Response(params: {

    /**
     * Pagination, sorting, and filter parameters for suppliers
     */
    body: PaginationWithFiltersRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<PaginatedResponseSupplierListDto>> {

    const rb = new RequestBuilder(this.rootUrl, SupplierV1Service.ListSuppliersV1Path, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaginatedResponseSupplierListDto>;
      })
    );
  }

  /**
   * List suppliers available for browsing.
   *
   * Deprecated: use /cursor-list instead. Provides list of suppliers. Suppliers are returned as unordered list.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listSuppliersV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   *
   * @deprecated
   */
  listSuppliersV1(params: {

    /**
     * Pagination, sorting, and filter parameters for suppliers
     */
    body: PaginationWithFiltersRequest
  },
  context?: HttpContext

): Observable<PaginatedResponseSupplierListDto> {

    return this.listSuppliersV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<PaginatedResponseSupplierListDto>) => r.body as PaginatedResponseSupplierListDto)
    );
  }

  /**
   * Path part for operation listSuppliersEligibleForUserCreationPaginatedV1
   */
  static readonly ListSuppliersEligibleForUserCreationPaginatedV1Path = '/v1/supplier/eligible-for-user-creation/list';

  /**
   * List suppliers eligible for user creation with pagination.
   *
   * Returns active suppliers that have at least one request past the Initial Stage, with pagination and search support. Preferred over the deprecated GET endpoint for better scalability with large supplier datasets.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listSuppliersEligibleForUserCreationPaginatedV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listSuppliersEligibleForUserCreationPaginatedV1$Response(params: {

    /**
     * Pagination, sorting, and filter parameters. Use supplierName filter with CONTAINING operator for search.
     */
    body: PaginationWithFiltersRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<PaginatedResponseSupplierListDto>> {

    const rb = new RequestBuilder(this.rootUrl, SupplierV1Service.ListSuppliersEligibleForUserCreationPaginatedV1Path, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaginatedResponseSupplierListDto>;
      })
    );
  }

  /**
   * List suppliers eligible for user creation with pagination.
   *
   * Returns active suppliers that have at least one request past the Initial Stage, with pagination and search support. Preferred over the deprecated GET endpoint for better scalability with large supplier datasets.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listSuppliersEligibleForUserCreationPaginatedV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listSuppliersEligibleForUserCreationPaginatedV1(params: {

    /**
     * Pagination, sorting, and filter parameters. Use supplierName filter with CONTAINING operator for search.
     */
    body: PaginationWithFiltersRequest
  },
  context?: HttpContext

): Observable<PaginatedResponseSupplierListDto> {

    return this.listSuppliersEligibleForUserCreationPaginatedV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<PaginatedResponseSupplierListDto>) => r.body as PaginatedResponseSupplierListDto)
    );
  }

  /**
   * Path part for operation checkDuplicateSuppliersV1
   */
  static readonly CheckDuplicateSuppliersV1Path = '/v1/supplier/duplicate-check';

  /**
   * Check for potential duplicate suppliers.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `checkDuplicateSuppliersV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  checkDuplicateSuppliersV1$Response(params: {

    /**
     * Candidate supplier name/email to check
     */
    body: SupplierDuplicateCheckRequestV1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<SupplierDuplicateCheckResultV1>> {

    const rb = new RequestBuilder(this.rootUrl, SupplierV1Service.CheckDuplicateSuppliersV1Path, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SupplierDuplicateCheckResultV1>;
      })
    );
  }

  /**
   * Check for potential duplicate suppliers.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `checkDuplicateSuppliersV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  checkDuplicateSuppliersV1(params: {

    /**
     * Candidate supplier name/email to check
     */
    body: SupplierDuplicateCheckRequestV1
  },
  context?: HttpContext

): Observable<SupplierDuplicateCheckResultV1> {

    return this.checkDuplicateSuppliersV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<SupplierDuplicateCheckResultV1>) => r.body as SupplierDuplicateCheckResultV1)
    );
  }

  /**
   * Path part for operation listSuppliersV1Cursor
   */
  static readonly ListSuppliersV1CursorPath = '/v1/supplier/cursor-list';

  /**
   * List suppliers using cursor-based pagination.
   *
   * Retrieves suppliers using continuation token pagination. Supports filtering and sorting. Maximum page size is 200.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listSuppliersV1Cursor()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listSuppliersV1Cursor$Response(params: {

    /**
     * Cursor pagination, sorting, and filter parameters
     */
    body: CursorPaginationRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CursorPaginatedResponseSupplierListV1Dto>> {

    const rb = new RequestBuilder(this.rootUrl, SupplierV1Service.ListSuppliersV1CursorPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CursorPaginatedResponseSupplierListV1Dto>;
      })
    );
  }

  /**
   * List suppliers using cursor-based pagination.
   *
   * Retrieves suppliers using continuation token pagination. Supports filtering and sorting. Maximum page size is 200.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listSuppliersV1Cursor$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listSuppliersV1Cursor(params: {

    /**
     * Cursor pagination, sorting, and filter parameters
     */
    body: CursorPaginationRequest
  },
  context?: HttpContext

): Observable<CursorPaginatedResponseSupplierListV1Dto> {

    return this.listSuppliersV1Cursor$Response(params,context).pipe(
      map((r: StrictHttpResponse<CursorPaginatedResponseSupplierListV1Dto>) => r.body as CursorPaginatedResponseSupplierListV1Dto)
    );
  }

  /**
   * Path part for operation getSupplierV1
   */
  static readonly GetSupplierV1Path = '/v1/supplier/{supplierId}';

  /**
   * Get a single supplier.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSupplierV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSupplierV1$Response(params: {
    supplierId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<SupplierListV1>> {

    const rb = new RequestBuilder(this.rootUrl, SupplierV1Service.GetSupplierV1Path, 'get');
    if (params) {
      rb.path('supplierId', params.supplierId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SupplierListV1>;
      })
    );
  }

  /**
   * Get a single supplier.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSupplierV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSupplierV1(params: {
    supplierId: string;
  },
  context?: HttpContext

): Observable<SupplierListV1> {

    return this.getSupplierV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<SupplierListV1>) => r.body as SupplierListV1)
    );
  }

  /**
   * Path part for operation updateSupplierV1
   */
  static readonly UpdateSupplierV1Path = '/v1/supplier/{supplierId}';

  /**
   * Update supplier details.
   *
   * Updates supplier information.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateSupplierV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSupplierV1$Response(params: {
    supplierId: string;

    /**
     * Supplier update data
     */
    body: SupplierUpdateCommandV1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<SupplierListV1>> {

    const rb = new RequestBuilder(this.rootUrl, SupplierV1Service.UpdateSupplierV1Path, 'patch');
    if (params) {
      rb.path('supplierId', params.supplierId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SupplierListV1>;
      })
    );
  }

  /**
   * Update supplier details.
   *
   * Updates supplier information.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateSupplierV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSupplierV1(params: {
    supplierId: string;

    /**
     * Supplier update data
     */
    body: SupplierUpdateCommandV1
  },
  context?: HttpContext

): Observable<SupplierListV1> {

    return this.updateSupplierV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<SupplierListV1>) => r.body as SupplierListV1)
    );
  }

}
